/* 
 * Copyright (C) 2022 SHC Holdings LLC.  All Rights Reserved. 
 */
import React, { useEffect } from 'react';
import { FormGroup, Label, TabPane } from 'reactstrap';
import {
  Checkbox,
  CheckboxGroup,
  useConditionCount,
  useFilterEditingValue,
  useLookupConditionSortByType,
  ConditionType,
  Select,
  CLAIM_NUMBERS,
  useSynchronizedPrimaryCondition,
  useGeneralConfigByDataSource,
  useMount,
  FilterDialogTabItemProps,
  useLookupCondition,
  FallbackComponent,
  Input,
  useEffectFilterEditingCount,
  CMSSourceTabSelector,
} from 'nmqf-common-ui';
import styled from 'styled-components';

const CLINICAL_TRIALS_CLAIM_DEFAULT = 2;

const StyledConditions = styled.div`
  display: flex;
  align-items: center;
`;
const StyledPrimaryCondition = styled.div`
  flex: 1;
`;
const StyledLabel = styled.div``;
const StyledClaim = styled.div`
  margin: 0.25rem 0;
`;
type ConditionSectionProps = Pick<FilterDialogTabItemProps, 'filterCountEffectDeps'>
export const ConditionSection: React.FC<ConditionSectionProps> = (props) => {
  const [textSearch] = useFilterEditingValue('textSearch');
  const [dataYear] = useFilterEditingValue('dataYear');
  const [cmsDataSource] = useFilterEditingValue('statistic', 'cmsDataSource');
  const [lookupData] = useLookupConditionSortByType(undefined, dataYear, cmsDataSource);
  const [selectingConditions, setSelectingConditions] = useFilterEditingValue('comorbidities');
  const primaryConditionWithoutClaims = useGeneralConfigByDataSource('primaryConditionWithoutClaims');

  const [doSynchronized] = useSynchronizedPrimaryCondition([ConditionType.Primary, ConditionType.Other]);

  const additionalConditions = lookupData.groupedCondition?.[ConditionType.Other];

  const [additionalConditionCount, fetchAdditionalConditionCount] = useConditionCount({ isPrimary: false });
  useEffectFilterEditingCount(
    (body) => {
      fetchAdditionalConditionCount({
        ...body,
        includedConditions: additionalConditions?.map(c => c.value)
      });
    },
    [selectingConditions, ...(props.filterCountEffectDeps || [])],
    'CONDITION'
  );

  useEffect(() => {
    doSynchronized();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmsDataSource, dataYear]);

  return (
    <>
      {
        (Array.isArray(primaryConditionWithoutClaims) && primaryConditionWithoutClaims.length)
          ? <PrimaryConditionWithoutClaims filterCountEffectDeps={props.filterCountEffectDeps} />
          : <ConditionWithinClaimSection />
      }
      <hr />
      <FormGroup tag="fieldset">
        <Label className="font-weight-bold">Additional Condition Filters</Label>
        <CheckboxGroup
          options={additionalConditions}
          formatOptionLabel={additionalConditionCount.getLabelWithCount}
          input={textSearch}
          noAll
          noSort
          value={selectingConditions}
          onChange={setSelectingConditions}
          sortValue={lookupData.sortValue}
        />
      </FormGroup>
    </>
  );
};

const PrimaryConditionWithoutClaims = ({ filterCountEffectDeps }: ConditionSectionProps) => {
  const [textSearch] = useFilterEditingValue('textSearch');
  const [dataYear] = useFilterEditingValue('dataYear');
  const [cmsDataSource] = useFilterEditingValue('statistic', 'cmsDataSource');
  const [lookupData] = useLookupConditionSortByType(undefined, dataYear, cmsDataSource);
  const primaryConditions = lookupData.groupedCondition?.[ConditionType.Primary];
  const [claimConditionsOptional, setClaimConditionsOptional] = useFilterEditingValue('primaryConditionWithClaimsOptionals');
  const [, setSelectingPrimaryConditions] = useFilterEditingValue('primaryCoMorbidities');
  const [, setClaimConditions] = useFilterEditingValue('primaryConditionWithClaims');
  const [comorbidities] = useFilterEditingValue('comorbidities');
  const [primaryConditionCount, fetchPrimaryConditionCount] = useConditionCount({ isPrimary: true });

  // reset Claim Option when the Primary Condition is not required the Claim number
  useMount(() => {
    setClaimConditions(undefined);
  })

  const filteredPrimaryConditions = textSearch
    ? primaryConditions?.filter((i) => i.label.toLowerCase().includes(textSearch?.toLowerCase()))
    : primaryConditions;

  useEffectFilterEditingCount(
    (body) => {
      fetchPrimaryConditionCount({
        ...body,
        includedConditions: filteredPrimaryConditions?.map(c => c.value),
      });
    },
    [comorbidities, ...(filterCountEffectDeps || [])],
    'PRIMARY_CONDITION'
  );

  const handleChangePrimaryConditionsWithoutClaims = (conditions: string[]) => {
    setClaimConditionsOptional(conditions);
    setSelectingPrimaryConditions(conditions);
  };

  return (
    <FormGroup tag="fieldset">
      <StyledConditions>
        <StyledPrimaryCondition>
          <Label className="font-weight-bold">Primary Conditions</Label>
        </StyledPrimaryCondition>
      </StyledConditions>
      {filteredPrimaryConditions?.map((item) => {
        return (
          <StyledConditions key={item.value}>
            <StyledPrimaryCondition>
              <Checkbox
                key={JSON.stringify(item.value)}
                value={item.value}
                payload={item}
                groupValue={claimConditionsOptional}
                label={primaryConditionCount.getLabelWithCount(item)}
                onChange={handleChangePrimaryConditionsWithoutClaims}
                sortValue={lookupData.sortValue}
              />
            </StyledPrimaryCondition>
          </StyledConditions>
        )
      })}
    </FormGroup>
  );
}

const ConditionWithinClaimSection = () => {
  const [textSearch] = useFilterEditingValue('textSearch');
  const [dataYear] = useFilterEditingValue('dataYear');
  const [countData] = useConditionCount();
  const [cmsDataSource] = useFilterEditingValue('statistic', 'cmsDataSource');
  const [lookupData] = useLookupConditionSortByType(undefined, dataYear, cmsDataSource);
  const primaryConditions = lookupData.groupedCondition?.[ConditionType.Primary];
  const [selectingPrimaryConditions, setSelectingPrimaryConditions] = useFilterEditingValue('primaryCoMorbidities');
  const [claimConditions, setClaimConditions] = useFilterEditingValue('primaryConditionWithClaims');

  useMount(() => {
    if (Array.isArray(selectingPrimaryConditions) && selectingPrimaryConditions.length) {
      handleChangePrimaryConditions(selectingPrimaryConditions)
    }
  })

  const filteredPrimaryConditions = textSearch
    ? primaryConditions?.filter((i) => i.label.toLowerCase().includes(textSearch?.toLowerCase()))
    : primaryConditions;

  const handleChangeClaim = (value: number, name: string) => {
    setClaimConditions({ ...claimConditions, [name]: value });
  };

  const handleChangePrimaryConditions = (conditions: string[]) => {
    setSelectingPrimaryConditions(conditions);
    setClaimConditions(
      conditions.reduce((acc, curr) => {
        acc[curr] = claimConditions?.[curr] ?? CLINICAL_TRIALS_CLAIM_DEFAULT;
        return acc;
      }, Object.assign({}))
    );
  };

  return (
    <FormGroup tag="fieldset">
      <StyledConditions>
        <StyledPrimaryCondition>
          <Label className="font-weight-bold">Primary Conditions</Label>
        </StyledPrimaryCondition>
        <StyledLabel>
          <Label className="font-weight-bold">Min # of Claims</Label>
        </StyledLabel>
      </StyledConditions>
      {filteredPrimaryConditions?.map((item) => {
        return (
          <StyledConditions key={item.value}>
            <StyledPrimaryCondition>
              <Checkbox
                key={JSON.stringify(item.value)}
                value={item.value}
                payload={item}
                groupValue={selectingPrimaryConditions}
                label={countData.getLabelWithCount(item)}
                onChange={handleChangePrimaryConditions}
                sortValue={lookupData.sortValue}
              />
            </StyledPrimaryCondition>
            <StyledClaim>
              <Select
                inline
                width="4.5"
                name={item.value}
                options={CLAIM_NUMBERS}
                optionsTransform={(cn: any) => ({ value: cn, label: cn })}
                value={claimConditions?.[item.value] || CLINICAL_TRIALS_CLAIM_DEFAULT}
                onChange={handleChangeClaim}
              />
            </StyledClaim>
          </StyledConditions>
        )
      })}
    </FormGroup>
  );
}

export const ConditionTab: React.FC<FilterDialogTabItemProps> = (props) => {
  const [textSearch, setTextSearch] = useFilterEditingValue('textSearch');
  const [lookupConditionData, fetchLookupCondition] = useLookupCondition();

  useMount(() => {
    fetchLookupCondition();
  });

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setTextSearch(value);
  };

  return (
    <TabPane tabId={props.tabId}>
      <FallbackComponent requesting={lookupConditionData.requesting} error={lookupConditionData.error} retry={fetchLookupCondition}>
        <Input className="flex-shrink-0 mb-2" placeholder="Search..." value={textSearch || ''} onChange={handleSearchInputChange} />
        {props.children}
      </FallbackComponent>
    </TabPane>
  );
};
ConditionTab.defaultProps = {
  tabId: 'ConditionTab',
  tabName: 'Conditions',
  tabSelector: (props) => <CMSSourceTabSelector {...props} type="disabled" />,
  filterCountEffectDeps: []
};
